import { useState, useEffect, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, 
  X, 
  Phone, 
  Mail, 
  MapPin, 
  Instagram, 
  Facebook,
  ChevronDown,
  Users,
  Target,
  Heart,
  TrendingUp,
  Award,
  Clock,
  CheckCircle,
  Globe,
  Star,
  Quote
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

// Language Context
const LanguageContext = createContext();

const useLanguage = () => useContext(LanguageContext);

// Translations
const translations = {
  es: {
    nav: {
      inicio: "Inicio",
      horarios: "Horarios",
      membresias: "Membresías",
      coaches: "Coaches",
      faq: "FAQ",
      contacto: "Contacto"
    },
    hero: {
      title: "¡Bienvenidos a",
      titleBreak: "CrossFit Río Grande!",
      tagline: "Primer gimnasio de cross training y entrenamiento funcional de Río Grande",
      subtitle: "Una comunidad que te ayudará a alcanzar tu máximo potencial - ¡físico y mental!",
      cta: "Reserva tu clase gratis",
      ctaSecondary: "Ver horarios"
    },
    whyUs: {
      title: "¿Por qué CrossFit Rio Grande?",
      benefits: [
        { title: "Comunidad y ambiente motivador", description: "Un grupo de personas comprometidas que se apoyan mutuamente" },
        { title: "Coaching atento y entrenamiento adaptable", description: "Entrenamiento supervisado y modificado según tus habilidades y necesidades" },
        { title: "Progreso físico y mental", description: "Mejora integral que transforma tu vida dentro y fuera del gym" },
        { title: "Apoyo del equipo", description: "¡Cuando uno gana, ganamos todos!" },
        { title: "Resultados extraordinarios y medibles", description: "Metas alcanzables con seguimiento constante de tu progreso" }
      ]
    },
    about: {
      title: "Sobre Nosotros",
      text: "Nuestra meta es ayudarte a alcanzar tus objetivos de salud, adaptando cada entrenamiento a tu necesidad. Aquí te sentirás inspirado a superar tus límites y alcanzarás resultados extraordinarios.",
      cta: "Únete a la comunidad"
    },
    schedule: {
      title: "Horarios",
      subtitle: "Encuentra tu clase",
      calendarTitle: "Calendario en Vivo",
      cta: "Reserva tu clase gratis",
      saturday: "Open Gym: 8:30AM - 10:30AM"
    },
    memberships: {
      title: "Membresías",
      subtitle: "Elige el plan que mejor se adapte a tus necesidades",
      consult: "Consultar",
      buyNow: "Comprar",
      freeClass: "Ofrecemos una clase gratis de prueba",
      dropin: {
        title: "DROP-IN",
        description: "¿De visita en Río Grande? Entrena con nosotros por un día. Perfecto para viajeros y atletas visitantes.",
        features: ["Una clase", "Sin compromiso", "Todos los niveles"]
      },
      plans: [
        {
          name: "BASIC",
          description: "Membresía mensual básica, ilimitada, para personas con y sin experiencia haciendo entrenamiento funcional.",
          features: ["Clases ilimitadas", "Acceso a todos los horarios", "Coaching personalizado"]
        },
        {
          name: "PARTNERS",
          description: "Membresía para ti y tu training partner: puede ser tu pareja, hijo/a, padre, o tu bro/bestie.",
          features: ["2 personas", "Clases ilimitadas", "Entrenar juntos", "Ahorro mensual"]
        },
        {
          name: "STUDENTS & SENIORS",
          description: "Membresía para estudiantes, universitarios y personas mayores de 62 años, al presentar ID o evidencia de matrícula.",
          features: ["Descuento especial", "Clases ilimitadas", "Requiere ID válido"]
        }
      ]
    },
    coaches: {
      title: "Nuestros Coaches",
      subtitle: "Conoce al equipo que te guiará en tu transformación"
    },
    faq: {
      title: "Preguntas Frecuentes",
      items: [
        {
          question: "¿Necesito experiencia previa?",
          answer: "No, no necesitas experiencia previa. Nuestros coaches adaptan cada entrenamiento a tu nivel de fitness y habilidades. Ya sea que nunca hayas hecho ejercicio o que seas un atleta experimentado, te guiaremos paso a paso."
        },
        {
          question: "¿Qué debo llevar a mi primera clase?",
          answer: "Trae ropa cómoda y deportiva, tenis apropiados para entrenar, una toalla pequeña y una botella de agua. Te recomendamos llegar 10-15 minutos antes de tu primera clase para conocer el gimnasio y hablar con tu coach."
        },
        {
          question: "¿Cómo reservo la clase gratis?",
          answer: "Puedes reservar tu clase gratis completando el formulario de contacto en esta página, llamando al 787-564-3757, o escribiéndonos por WhatsApp. Te contactaremos para confirmar tu horario."
        },
        {
          question: "¿Qué es CrossFit y cómo se escala?",
          answer: "CrossFit es un programa de entrenamiento funcional de alta intensidad que combina movimientos de levantamiento de pesas, gimnasia y cardio. Todos los ejercicios son escalables, lo que significa que podemos ajustar el peso, las repeticiones o el movimiento según tu capacidad actual."
        },
        {
          question: "¿Hay Open Gym los sábados?",
          answer: "Sí, los sábados ofrecemos Open Gym de 8:30AM a 10:30AM. Durante este tiempo puedes trabajar en tus debilidades, practicar movimientos específicos o completar entrenamientos que hayas perdido durante la semana."
        },
        {
          question: "¿Qué incluye una membresía ilimitada?",
          answer: "La membresía ilimitada incluye acceso a todas las clases grupales en cualquier horario, coaching personalizado durante las clases, acceso al Open Gym de los sábados, y ser parte de nuestra comunidad con eventos especiales y competencias."
        }
      ]
    },
    contact: {
      title: "Contáctanos",
      subtitle: "Reserva tu clase gratis y comienza tu transformación hoy",
      infoTitle: "Información de Contacto",
      formHeader: "¡Reserva tu clase gratis!"
    },
    cta: {
      title: "Agenda tu clase gratis y empieza hoy",
      subtitle: "No esperes más para transformar tu vida. Únete a la comunidad CrossFit Rio Grande.",
      button: "Reserva tu clase gratis"
    },
    footer: {
      tagline: "El primer gimnasio de cross training en Río Grande, Puerto Rico.",
      navigation: "Navegación",
      members: "Para Miembros",
      pauseMembership: "Pausar Membresía",
      cancelMembership: "Cancelar Membresía",
      bookClass: "Reservar Clase",
      contact: "Contacto",
      rights: "Todos los derechos reservados."
    },
    testimonials: {
      title: "Lo Que Dicen Nuestros Miembros",
      subtitle: "Reseñas reales de nuestra comunidad",
      googleReviews: "Reseñas de Google"
    }
  },
  en: {
    nav: {
      inicio: "Home",
      horarios: "Schedule",
      membresias: "Memberships",
      coaches: "Coaches",
      faq: "FAQ",
      contacto: "Contact"
    },
    hero: {
      title: "Welcome to",
      titleBreak: "CrossFit Río Grande!",
      tagline: "First cross training and functional training gym in Río Grande",
      subtitle: "A community that will help you reach your full potential - physical and mental!",
      cta: "Book your free class",
      ctaSecondary: "View schedule"
    },
    whyUs: {
      title: "Why CrossFit Rio Grande?",
      benefits: [
        { title: "Community and motivating environment", description: "A group of committed people who support each other" },
        { title: "Attentive coaching and adaptable training", description: "Supervised and modified training according to your abilities and needs" },
        { title: "Physical and mental progress", description: "Comprehensive improvement that transforms your life inside and outside the gym" },
        { title: "Team support", description: "When one wins, we all win!" },
        { title: "Extraordinary and measurable results", description: "Achievable goals with constant tracking of your progress" }
      ]
    },
    about: {
      title: "About Us",
      text: "Our goal is to help you achieve your health goals, adapting each workout to your needs. Here you will feel inspired to push your limits and achieve extraordinary results.",
      cta: "Join the community"
    },
    schedule: {
      title: "Schedule",
      subtitle: "Find your class",
      calendarTitle: "Live Calendar",
      cta: "Book your free class",
      saturday: "Open Gym: 8:30AM - 10:30AM"
    },
    memberships: {
      title: "Memberships",
      subtitle: "Choose the plan that best suits your needs",
      consult: "Inquire",
      buyNow: "Buy Now",
      freeClass: "We offer a free trial class",
      dropin: {
        title: "DROP-IN",
        description: "Visiting Río Grande? Train with us for a day. Perfect for travelers and visiting athletes.",
        features: ["One class", "No commitment", "All levels"]
      },
      plans: [
        {
          name: "BASIC",
          description: "Basic monthly membership, unlimited, for people with and without functional training experience.",
          features: ["Unlimited classes", "Access to all schedules", "Personalized coaching"]
        },
        {
          name: "PARTNERS",
          description: "Membership for you and your training partner: can be your spouse, child, parent, or your bro/bestie.",
          features: ["2 people", "Unlimited classes", "Train together", "Monthly savings"]
        },
        {
          name: "STUDENTS & SENIORS",
          description: "Membership for students, college students, and people over 62, upon presenting ID or enrollment evidence.",
          features: ["Special discount", "Unlimited classes", "Valid ID required"]
        }
      ]
    },
    coaches: {
      title: "Our Coaches",
      subtitle: "Meet the team that will guide you in your transformation"
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          question: "Do I need previous experience?",
          answer: "No, you don't need previous experience. Our coaches adapt each workout to your fitness level and abilities. Whether you've never exercised or you're an experienced athlete, we'll guide you step by step."
        },
        {
          question: "What should I bring to my first class?",
          answer: "Bring comfortable athletic clothing, appropriate training shoes, a small towel, and a water bottle. We recommend arriving 10-15 minutes before your first class to get to know the gym and talk to your coach."
        },
        {
          question: "How do I book the free class?",
          answer: "You can book your free class by completing the contact form on this page, calling 787-564-3757, or messaging us on WhatsApp. We'll contact you to confirm your schedule."
        },
        {
          question: "What is CrossFit and how does it scale?",
          answer: "CrossFit is a high-intensity functional training program that combines weightlifting movements, gymnastics, and cardio. All exercises are scalable, meaning we can adjust the weight, repetitions, or movement according to your current ability."
        },
        {
          question: "Is there Open Gym on Saturdays?",
          answer: "Yes, on Saturdays we offer Open Gym from 8:30AM to 10:30AM. During this time you can work on your weaknesses, practice specific movements, or complete workouts you missed during the week."
        },
        {
          question: "What does an unlimited membership include?",
          answer: "The unlimited membership includes access to all group classes at any time, personalized coaching during classes, access to Saturday Open Gym, and being part of our community with special events and competitions."
        }
      ]
    },
    contact: {
      title: "Contact Us",
      subtitle: "Book your free class and start your transformation today",
      infoTitle: "Contact Information",
      formHeader: "Book your free class!"
    },
    cta: {
      title: "Schedule your free class and start today",
      subtitle: "Don't wait any longer to transform your life. Join the CrossFit Rio Grande community.",
      button: "Book your free class"
    },
    footer: {
      tagline: "The first cross training gym in Río Grande, Puerto Rico.",
      navigation: "Navigation",
      members: "For Members",
      pauseMembership: "Pause Membership",
      cancelMembership: "Cancel Membership",
      bookClass: "Book Class",
      contact: "Contact",
      rights: "All rights reserved."
    },
    testimonials: {
      title: "What Our Members Say",
      subtitle: "Real reviews from our community",
      googleReviews: "Google Reviews"
    }
  }
};

// Testimonials data
const TESTIMONIALS = {
  es: [
    {
      name: "Jailene V",
      rating: 5,
      text: "Los coaches son unos duros! Fuimos un grupo de principiantes y nos explicaron la rutina con paciencia, nos dieron modificaciones, e hicimos warm-up, el workout y cooldown. También están pendientes de que te posiciones bien para que no te lastimes y te motivan mientras haces los ejercicios."
    },
    {
      name: "N Roó",
      rating: 5,
      text: "Eh entrenado con ellos desde hace 2 años antes de que abrieran el local. Los coach son los mejores. Profesionales, educados en el deporte, modifican los ejercicios dependiendo de tu rendimiento y fortalezas. Siempre están pendientes a la ejecución para evitar que uno se lastime, motivan durante el entrenamiento. Aconsejan sobre nutrición y qué hacer fuera del CrossFit para mejorar el rendimiento."
    },
    {
      name: "Limary Lopez",
      rating: 5,
      text: "Me entrenan ajustándose a mi necesidad y condición física 🤰"
    },
    {
      name: "Julio Gonzalez",
      rating: 5,
      text: "El coach es muy considerado. Te ayuda a mejorar y modifica los ejercicios para tus capacidades."
    }
  ],
  en: [
    {
      name: "Jailene V",
      rating: 5,
      text: "The coaches are amazing! We went as a group of beginners and they explained the routine patiently, gave us modifications, and we did warm-up, workout, and cooldown. They also make sure you position yourself correctly so you don't get hurt and motivate you while you exercise."
    },
    {
      name: "N Roó",
      rating: 5,
      text: "I've been training with them for 2 years before they opened the gym. The coaches are the best. Professional, educated in the sport, they modify exercises depending on your performance and strengths. They're always watching your form to prevent injuries, motivate during training. They advise on nutrition and what to do outside CrossFit to improve performance."
    },
    {
      name: "Limary Lopez",
      rating: 5,
      text: "They train me adjusting to my needs and physical condition 🤰"
    },
    {
      name: "Julio Gonzalez",
      rating: 5,
      text: "The coach is very considerate. He helps you improve and modifies exercises for your abilities."
    }
  ]
};

// Assets
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/kg43iruu_107677_CrossFit%20Rio%20Grande_Flat_HP_DK_01.png";
const LOGO_HEADER = "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/65bmqjjt_107677_CrossFit%20Rio%20Grande_Flat_HP_DK_02.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/2c7sfb97_107677_CrossFit%20Rio%20Grande_Flat_HP_DK_05.png";

// Contact Info
const CONTACT = {
  phone: "787-564-3757",
  email: "bengie.lopez@crossfitriogrande.com",
  address: "94HM+26C, La Dolores, Río Grande 00745",
  instagram: "https://instagram.com/crossfitriogrande",
  facebook: "https://www.facebook.com/p/CrossFit-Rio-Grande-61571947433070/",
  whatsapp: "https://wa.me/17875643757",
  pushpress: "https://riograndecrosstraining.pushpress.com/landing/plans/plan_73ed03d25b0f43/login",
  pushpressDropin: "https://riograndecrosstraining.pushpress.com/landing/plans/plan_d242dd6b3fa243/login",
  pushpressCalendar: "https://riograndecrosstraining.pushpress.com/landing/calendar",
  mondayPausa: "https://forms.monday.com/forms/8fca73f771dbf72c801d528a86a3a4ec?r=use1",
  mondayCancelacion: "https://forms.monday.com/forms/fafc85f4d4250a5532becc3f4635b507?r=use1&cancelaciones="
};

// Hero background image
const HERO_BG = "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/3udot9oo_b_modifica_esta_foto_p.jpeg";

// Schedule data
const SCHEDULE = {
  es: [
    { day: "Lunes", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM", "7:30PM"] },
    { day: "Martes", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM", "7:30PM"] },
    { day: "Miércoles", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM", "7:30PM"] },
    { day: "Jueves", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM", "7:30PM"] },
    { day: "Viernes", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM"] },
    { day: "Sábado", times: ["Open Gym: 8:30AM - 10:30AM"] }
  ],
  en: [
    { day: "Monday", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM", "7:30PM"] },
    { day: "Tuesday", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM", "7:30PM"] },
    { day: "Wednesday", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM", "7:30PM"] },
    { day: "Thursday", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM", "7:30PM"] },
    { day: "Friday", times: ["5:30AM", "6:30AM", "5:30PM", "6:30PM"] },
    { day: "Saturday", times: ["Open Gym: 8:30AM - 10:30AM"] }
  ]
};

// Coaches data with translations
const COACHES_DATA = {
  es: [
    {
      name: "Bengie López",
      role: "Dueño y Head Coach",
      bio: "CrossFit Level 2 Trainer, pro-wrestler y profesional de ciberseguridad. Tiene más de 15 años de experiencia como atleta, 10+ como atleta de CrossFit. Tiene conocimientos en artes marciales de defensa personal, con formación en Aikido y Brazilian Jiu Jitsu. Además de CrossFit, entrena y hace lucha libre.",
      image: "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/n36u15vn_PWR00018.jpg"
    },
    {
      name: "Coraly López",
      role: "Dueña y Coach",
      bio: "CrossFit Level 1 Trainer. Lleva más de 10 años entrenando bajo la metodología de CrossFit, y ha practicado natación y Brazilian Jiu Jitsu. Su benchmark favorito es Randy. Encargada de la experiencia del cliente, se ocupa del manejo de membresías y comunicaciones. Además de estudiar Administración de Empresas, estudió diseño y confección de moda de alta costura.",
      image: "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/c69hcpdf_PWR00003.jpg"
    },
    {
      name: "Carlos Cortés (Carlitos)",
      role: "Coach",
      bio: "CrossFit Level 1 Trainer. Carlos forma parte del equipo de coaches de CrossFit Rio Grande y se destaca por su excelente movilidad. Su movimiento favorito es el toes-to-bar, y su enfoque como coach está en ayudar a cada atleta a moverse bien, mejorar su técnica y desarrollar confianza a través de la consistencia en el entrenamiento.",
      image: "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/vojke1al_PWR00053.jpg"
    },
    {
      name: "Yared Vázquez",
      role: "Coach",
      bio: "CrossFit Level 1 Trainer. Yared forma parte del equipo de coaches de CrossFit Rio Grande y se distingue por su gran fortaleza, particularmente en el deadlift. Como coach, aporta disciplina, presencia y un enfoque en ayudar a los atletas a desarrollar consistencia, fuerza y seguridad en cada entrenamiento.",
      image: "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/03pm7qr6_PWR00074.jpg"
    }
  ],
  en: [
    {
      name: "Bengie López",
      role: "Owner & Head Coach",
      bio: "CrossFit Level 2 Trainer, pro-wrestler, and cybersecurity professional. He has over 15 years of experience as an athlete, 10+ as a CrossFit athlete. He has knowledge in self-defense martial arts, with training in Aikido and Brazilian Jiu Jitsu. Besides CrossFit, he trains and does wrestling.",
      image: "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/n36u15vn_PWR00018.jpg"
    },
    {
      name: "Coraly López",
      role: "Owner & Coach",
      bio: "CrossFit Level 1 Trainer. She has been training under the CrossFit methodology for over 10 years, and has practiced swimming and Brazilian Jiu Jitsu. Her favorite benchmark is Randy. In charge of customer experience, she handles membership management and communications. Besides studying Business Administration, she studied haute couture fashion design and dressmaking.",
      image: "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/c69hcpdf_PWR00003.jpg"
    },
    {
      name: "Carlos Cortés (Carlitos)",
      role: "Coach",
      bio: "CrossFit Level 1 Trainer. Carlos is part of the CrossFit Rio Grande coaching team and stands out for his excellent mobility. His favorite movement is the toes-to-bar, and his focus as a coach is on helping each athlete move well, improve their technique, and develop confidence through training consistency.",
      image: "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/vojke1al_PWR00053.jpg"
    },
    {
      name: "Yared Vázquez",
      role: "Coach",
      bio: "CrossFit Level 1 Trainer. Yared is part of the CrossFit Rio Grande coaching team and is distinguished by his great strength, particularly in the deadlift. As a coach, he brings discipline, presence, and a focus on helping athletes develop consistency, strength, and confidence in every workout.",
      image: "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/03pm7qr6_PWR00074.jpg"
    }
  ]
};

// About section image
const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_crossfit-rio-grande/artifacts/3d24oxf3_PWR07702.jpg";

// Benefit icons mapping
const BENEFIT_ICONS = [Users, Target, Heart, TrendingUp, Award];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Header Component
const Header = ({ scrolled, hidden, mobileMenuOpen, setMobileMenuOpen }) => {
  const { lang, setLang, t } = useLanguage();
  
  const navItems = [
    { label: t.nav.inicio, href: "#inicio" },
    { label: t.nav.horarios, href: "#horarios" },
    { label: t.nav.membresias, href: "#membresias" },
    { label: t.nav.coaches, href: "#coaches" },
    { label: t.nav.faq, href: "#faq" },
    { label: t.nav.contacto, href: "#contacto" }
  ];

  return (
    <header 
      className={`fixed top-4 left-4 right-4 z-50 transition-all duration-300 ${
        hidden ? '-translate-y-[calc(100%+1.5rem)]' : 'translate-y-0'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto bg-[#0A0A0A]/85 backdrop-blur-md shadow-2xl rounded-full px-6 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#inicio" className="flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_HEADER} 
              alt="CrossFit Rio Grande" 
              className="h-32 md:h-40 w-auto -my-6"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="nav-link text-sm font-semibold uppercase tracking-wider transition-colors whitespace-nowrap text-white hover:text-[#22C55E]"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Language Toggle + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
              className="flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-sm uppercase tracking-wider transition-all text-white hover:text-[#22C55E] border border-white/40 hover:border-[#22C55E]"
              data-testid="language-toggle"
            >
              <Globe size={16} />
              {lang === 'es' ? 'EN' : 'ES'}
            </button>
            <a
              href="#contacto"
              className="btn-primary text-sm rounded-full"
              data-testid="header-cta"
            >
              {t.hero.cta}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="text-white" size={28} />
            ) : (
              <Menu className="text-white" size={28} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu (rendered via portal to escape header's backdrop-filter containing block) */}
      {createPortal(
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 top-28 bg-white z-[60] lg:hidden"
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col p-6 space-y-4">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-xl font-bold uppercase tracking-wider text-gray-800 hover:text-[#22C55E] py-3 border-b border-gray-100"
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </a>
                ))}
                
                {/* Mobile Language Toggle */}
                <button
                  onClick={() => {
                    setLang(lang === 'es' ? 'en' : 'es');
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-center gap-2 py-3 text-lg font-bold uppercase tracking-wider text-gray-600 hover:text-[#22C55E] border-b border-gray-100"
                  data-testid="mobile-language-toggle"
                >
                  <Globe size={20} />
                  {lang === 'es' ? 'English' : 'Español'}
                </button>

                <a
                  href="#contacto"
                  onClick={() => setMobileMenuOpen(false)}
                  className="btn-primary text-center mt-6"
                  data-testid="mobile-cta"
                >
                  {t.hero.cta}
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
};

// Hero Section
const Hero = () => {
  const { t } = useLanguage();
  return (
    <section 
      id="inicio" 
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.5), rgba(0,0,0,0.4)), url(${HERO_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-32 text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="space-y-8"
        >
          <motion.h1 
            variants={fadeInUp}
            className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-tight"
            data-testid="hero-title"
          >
            {t.hero.title}<br />{t.hero.titleBreak}
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-xl md:text-2xl text-[#22C55E] font-bold uppercase tracking-wider"
            data-testid="hero-tagline"
          >
            {t.hero.tagline}
          </motion.p>

          <motion.p 
            variants={fadeInUp}
            className="text-base md:text-lg text-gray-200 max-w-3xl mx-auto leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
          >
            <a 
              href="#contacto"
              className="btn-primary w-full sm:w-auto"
              data-testid="hero-cta-primary"
            >
              {t.hero.cta}
            </a>
            <a 
              href="#horarios" 
              className="btn-secondary w-full sm:w-auto"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <ChevronDown className="text-white/60" size={32} />
      </motion.div>
    </section>
  );
};

// Why Us Section
const WhyUs = () => {
  const { t } = useLanguage();
  return (
    <section id="porque" className="py-20 md:py-32 bg-white" data-testid="whyus-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0A0A0A] mb-4"
          >
            {t.whyUs.title}
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {t.whyUs.benefits.map((benefit, index) => {
            const Icon = BENEFIT_ICONS[index];
            return (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-[#F0FDF4] p-8 card-hover group"
                data-testid={`benefit-${index}`}
              >
                <Icon 
                  className="w-12 h-12 text-[#22C55E] mb-4 group-hover:scale-110 transition-transform" 
                />
                <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  const { t } = useLanguage();
  return (
    <section id="nosotros" className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <img 
              src={ABOUT_IMAGE}
              alt="Comunidad CrossFit Rio Grande"
              className="w-full h-[400px] object-cover"
            />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-6"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl lg:text-5xl font-black text-white"
            >
              {t.about.title}
            </motion.h2>
            
            <motion.p 
              variants={fadeInUp}
              className="text-gray-300 text-lg leading-relaxed"
            >
              {t.about.text}
            </motion.p>

            <motion.a
              variants={fadeInUp}
              href="#contacto"
              className="btn-primary inline-block"
              data-testid="about-cta"
            >
              {t.about.cta}
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Schedule Section
const Schedule = () => {
  const { lang, t } = useLanguage();
  const scheduleData = SCHEDULE[lang];
  
  return (
    <section id="horarios" className="py-20 md:py-32 bg-white" data-testid="schedule-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0A0A0A] mb-4"
          >
            {t.schedule.title}
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-gray-600 max-w-2xl mx-auto"
          >
            {t.schedule.subtitle}
          </motion.p>
        </motion.div>

        {/* Static Schedule Reference */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mb-12"
        >
          {/* Desktop Table */}
          <table className="hidden md:table w-full schedule-table mb-8">
            <thead>
              <tr>
                {scheduleData.map((item) => (
                  <th key={item.day} className="px-4 py-3 text-left text-sm">
                    {item.day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {scheduleData.map((item) => (
                  <td key={item.day} className="px-4 py-4 align-top">
                    <div className="space-y-1">
                      {item.times.map((time, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-2 text-gray-700 text-sm"
                        >
                          <Clock size={14} className="text-[#22C55E]" />
                          <span>{time}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>

          {/* Mobile Cards */}
          <div className="md:hidden grid grid-cols-2 gap-3 mb-8">
            {scheduleData.map((item) => (
              <div 
                key={item.day}
                className="bg-[#F0FDF4] p-4"
                data-testid={`schedule-${item.day.toLowerCase()}`}
              >
                <h3 className="text-base font-bold text-[#0A0A0A] mb-2">{item.day}</h3>
                <div className="space-y-1">
                  {item.times.map((time, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-1 text-gray-700 text-xs"
                    >
                      <Clock size={12} className="text-[#22C55E]" />
                      <span>{time}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Live Calendar from PushPress */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="bg-gray-50 rounded-sm overflow-hidden"
        >
          <div className="bg-[#0A0A0A] px-6 py-4 flex items-center justify-between">
            <h3 className="text-white font-bold uppercase tracking-wider">
              {t.schedule.calendarTitle}
            </h3>
            <a
              href="#contacto"
              className="bg-[#22C55E] hover:bg-[#16A34A] text-white text-sm font-bold uppercase tracking-wider px-4 py-2 transition-colors"
              data-testid="calendar-reserve-btn"
            >
              {t.schedule.cta}
            </a>
          </div>
          <iframe
            src={`${CONTACT.pushpressCalendar}?framed=1`}
            width="100%"
            height="700"
            frameBorder="0"
            style={{ border: 0 }}
            title="Calendario de Clases - CrossFit Rio Grande"
            data-testid="pushpress-calendar"
          />
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center mt-12"
        >
          <a 
            href="#contacto"
            className="btn-primary"
            data-testid="schedule-cta"
          >
            {t.schedule.cta}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Memberships Section
const Memberships = () => {
  const { t } = useLanguage();
  return (
    <section id="membresias" className="py-20 md:py-32 section-accent" data-testid="memberships-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0A0A0A] mb-4"
          >
            {t.memberships.title}
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-gray-600 max-w-2xl mx-auto"
          >
            {t.memberships.subtitle}
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {/* Drop-In Card */}
          <motion.div
            variants={fadeInUp}
            className="bg-[#0A0A0A] border border-gray-800 p-8 card-hover flex flex-col"
            data-testid="membership-dropin"
          >
            <h3 className="text-2xl font-black text-[#22C55E] mb-4">
              {t.memberships.dropin.title}
            </h3>
            <p className="text-gray-400 mb-6 flex-grow">
              {t.memberships.dropin.description}
            </p>
            <ul className="space-y-3 mb-8">
              {t.memberships.dropin.features.map((feature, fIndex) => (
                <li key={fIndex} className="flex items-center gap-2 text-gray-300">
                  <CheckCircle size={18} className="text-[#22C55E] flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <a 
              href={CONTACT.pushpressDropin}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-center"
              data-testid="membership-cta-dropin"
            >
              {t.memberships.buyNow}
            </a>
          </motion.div>

          {/* Regular Membership Plans */}
          {t.memberships.plans.map((membership, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-white border border-gray-100 p-8 card-hover flex flex-col"
              data-testid={`membership-${membership.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <h3 className="text-2xl font-black text-[#22C55E] mb-4">
                {membership.name}
              </h3>
              <p className="text-gray-600 mb-6 flex-grow">
                {membership.description}
              </p>
              <ul className="space-y-3 mb-8">
                {membership.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle size={18} className="text-[#22C55E] flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <a 
                href="#contacto"
                className="btn-dark text-center"
                data-testid={`membership-cta-${index}`}
              >
                {t.memberships.consult}
              </a>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mt-12 text-center"
        >
          <a
            href="#contacto"
            className="inline-flex items-center gap-3 bg-[#22C55E] hover:bg-[#16A34A] text-white px-6 py-4 transition-colors cursor-pointer"
            data-testid="memberships-freeclass-cta"
          >
            <Award size={24} />
            <span className="font-bold uppercase tracking-wider">
              {t.memberships.freeClass}
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Coaches Section
const CoachesSection = () => {
  const { lang, t } = useLanguage();
  const coaches = COACHES_DATA[lang];
  
  return (
    <section id="coaches" className="py-20 md:py-32 bg-white" data-testid="coaches-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0A0A0A] mb-4"
          >
            {t.coaches.title}
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-gray-600 max-w-2xl mx-auto"
          >
            {t.coaches.subtitle}
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-12"
        >
          {coaches.map((coach, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-[#F0FDF4] overflow-hidden"
              data-testid={`coach-${index}`}
            >
              <div className="aspect-square overflow-hidden bg-[#0A0A0A]">
                <img 
                  src={coach.image}
                  alt={coach.name}
                  className="w-full h-full object-cover"
                  style={{ 
                    objectPosition: index >= 2 ? 'center 15%' : 'center top'
                  }}
                />
              </div>
              <div className="p-8">
                <span className="text-[#22C55E] font-bold uppercase tracking-wider text-sm">
                  {coach.role}
                </span>
                <h3 className="text-2xl font-black text-[#0A0A0A] mt-2 mb-4">
                  {coach.name}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {coach.bio}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { lang, t } = useLanguage();
  const testimonials = TESTIMONIALS[lang];

  return (
    <section id="testimonios" className="py-20 md:py-32 section-accent" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0A0A0A] mb-4"
          >
            {t.testimonials.title}
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-gray-600 max-w-2xl mx-auto"
          >
            {t.testimonials.subtitle}
          </motion.p>
          <motion.div 
            variants={fadeInUp}
            className="flex items-center justify-center gap-2 mt-4"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <span className="text-gray-600 font-medium">5.0 - {t.testimonials.googleReviews}</span>
          </motion.div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-6"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-white p-6 md:p-8 card-hover relative"
              data-testid={`testimonial-${index}`}
            >
              <Quote className="absolute top-4 right-4 text-[#22C55E]/20" size={40} />
              
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              
              {/* Review text */}
              <p className="text-gray-700 leading-relaxed mb-6 relative z-10">
                "{testimonial.text}"
              </p>
              
              {/* Reviewer */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#22C55E] rounded-full flex items-center justify-center text-white font-bold">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-[#0A0A0A]">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">Google Review</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const { t } = useLanguage();
  return (
    <section id="faq" className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4"
          >
            {t.faq.title}
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <Accordion type="single" collapsible className="space-y-2">
            {t.faq.items.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-gray-800 bg-gray-900/50 px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-white hover:text-[#22C55E] font-bold uppercase tracking-wider text-left py-6 text-base md:text-lg">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 pb-6 text-base md:text-lg leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const { t } = useLanguage();
  return (
    <section id="contacto" className="py-20 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0A0A0A] mb-4"
          >
            {t.contact.title}
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-gray-600 max-w-2xl mx-auto"
          >
            {t.contact.subtitle}
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* PushPress Embedded Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-gray-50 rounded-sm overflow-hidden"
          >
            <div className="bg-[#22C55E] px-6 py-4">
              <h3 className="text-white font-black uppercase tracking-wider text-lg sm:text-xl" data-testid="form-header">
                {t.contact.formHeader}
              </h3>
            </div>
            <div className="relative overflow-hidden h-[1000px] sm:h-[870px] md:h-[800px]">
              <iframe
                src="https://riograndecrosstraining.pushpress.com/open/interested?framed=1"
                width="100%"
                height="1700"
                frameBorder="0"
                style={{ border: 0, display: 'block', marginTop: '-410px' }}
                title="Reserva tu clase gratis - CrossFit Rio Grande"
                data-testid="pushpress-form"
              />
            </div>
          </motion.div>

          {/* Contact Info & Map */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="space-y-8"
          >
            {/* Contact Info */}
            <div className="bg-[#F0FDF4] p-8 space-y-6">
              <h3 className="text-xl font-black text-[#0A0A0A]">{t.contact.infoTitle}</h3>
              
              <div className="space-y-4">
                <a 
                  href={`tel:${CONTACT.phone}`}
                  className="flex items-center gap-4 text-gray-700 hover:text-[#22C55E] transition-colors"
                  data-testid="contact-phone"
                >
                  <Phone className="text-[#22C55E]" size={24} />
                  <span className="font-medium">{CONTACT.phone}</span>
                </a>
                
                <a 
                  href={`mailto:${CONTACT.email}`}
                  className="flex items-center gap-4 text-gray-700 hover:text-[#22C55E] transition-colors"
                  data-testid="contact-email"
                >
                  <Mail className="text-[#22C55E]" size={24} />
                  <span className="font-medium">{CONTACT.email}</span>
                </a>
                
                <div className="flex items-start gap-4 text-gray-700">
                  <MapPin className="text-[#22C55E] flex-shrink-0" size={24} />
                  <span className="font-medium">{CONTACT.address}</span>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex gap-4 pt-4">
                <a 
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-[#0A0A0A] text-white hover:bg-[#22C55E] transition-colors"
                  data-testid="social-instagram"
                >
                  <Instagram size={24} />
                </a>
                <a 
                  href={CONTACT.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-[#0A0A0A] text-white hover:bg-[#22C55E] transition-colors"
                  data-testid="social-facebook"
                >
                  <Facebook size={24} />
                </a>
                <a 
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-[#22C55E] text-white hover:bg-[#16A34A] transition-colors"
                  data-testid="social-whatsapp"
                >
                  <Phone size={24} />
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="h-[300px] bg-gray-100">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3783.8!2d-65.8314!3d18.3822!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8c028e5e5d3f0001%3A0x0!2sR%C3%ADo%20Grande%2C%20Puerto%20Rico!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="CrossFit Rio Grande Location"
                data-testid="map-iframe"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 bg-[#22C55E]" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="space-y-6"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-white"
          >
            {t.cta.title}
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-white/90 text-lg"
          >
            {t.cta.subtitle}
          </motion.p>
          <motion.div variants={fadeInUp}>
            <a 
              href="#contacto"
              className="inline-block bg-[#0A0A0A] hover:bg-white hover:text-[#0A0A0A] text-white font-bold uppercase tracking-wider px-10 py-5 transition-all duration-300"
              data-testid="final-cta"
            >
              {t.cta.button}
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  
  const navItems = [
    { label: t.nav.inicio, href: "#inicio" },
    { label: t.nav.horarios, href: "#horarios" },
    { label: t.nav.membresias, href: "#membresias" },
    { label: t.nav.coaches, href: "#coaches" },
    { label: t.nav.faq, href: "#faq" },
    { label: t.nav.contacto, href: "#contacto" }
  ];

  return (
    <footer className="bg-[#0A0A0A] py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL} 
              alt="CrossFit Rio Grande" 
              className="w-full max-w-[280px] h-auto"
            />
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-4 text-sm">{t.footer.navigation}</h4>
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-gray-400 hover:text-[#22C55E] text-sm transition-colors"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Para Miembros */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-4 text-sm">{t.footer.members}</h4>
            <nav className="flex flex-col gap-2">
              <a
                href={CONTACT.mondayPausa}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#22C55E] text-sm transition-colors"
                data-testid="footer-pausa-membresia"
              >
                {t.footer.pauseMembership}
              </a>
              <a
                href={CONTACT.mondayCancelacion}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#22C55E] text-sm transition-colors"
                data-testid="footer-cancelacion"
              >
                {t.footer.cancelMembership}
              </a>
              <a
                href="#contacto"
                className="text-gray-400 hover:text-[#22C55E] text-sm transition-colors"
              >
                {t.footer.bookClass}
              </a>
            </nav>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-4 text-sm">{t.footer.contact}</h4>
            <div className="space-y-2 mb-4">
              <a href={`tel:${CONTACT.phone}`} className="text-gray-400 hover:text-[#22C55E] text-sm block transition-colors">
                {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="text-gray-400 hover:text-[#22C55E] text-sm block transition-colors break-all">
                {CONTACT.email}
              </a>
            </div>
            <div className="flex gap-3">
              <a 
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#22C55E] transition-colors"
              >
                <Instagram size={20} />
              </a>
              <a 
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#22C55E] transition-colors"
              >
                <Facebook size={20} />
              </a>
              <a 
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#22C55E] transition-colors"
              >
                <Phone size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} CrossFit Rio Grande. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const WhatsAppFloat = () => {
  const { lang } = useLanguage();
  const tooltip = lang === 'es' ? '¿Preguntas? ¡Chatea con nosotros!' : 'Questions? Chat with us!';

  return (
    <motion.a
      href={CONTACT.whatsapp}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp"
      data-testid="whatsapp-float"
      initial={{ opacity: 0, scale: 0, y: 40 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: 1.2, type: "spring", stiffness: 260, damping: 20 }}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.95 }}
      className="fixed bottom-6 right-6 z-[100] group flex items-center"
    >
      {/* Tooltip bubble (desktop only) */}
      <span className="hidden md:block absolute right-full mr-3 whitespace-nowrap bg-white text-[#0A0A0A] text-sm font-semibold px-4 py-2 rounded-full shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
        {tooltip}
      </span>

      {/* Pulse ring */}
      <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping" aria-hidden="true" />

      {/* Button */}
      <span className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] hover:bg-[#1FB455] shadow-2xl transition-colors">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
          className="w-7 h-7 md:w-8 md:h-8 fill-white"
          aria-hidden="true"
        >
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39-.129 0-.304-.053-.442-.139-.592-.322-1.693-.977-2.717-1.895-.229-.202-.558-.616-.674-.784-.194-.276-.194-.391-.058-.575.133-.191.382-.483.571-.698.158-.177.233-.336.35-.564.07-.165.05-.29-.013-.412-.075-.174-.6-1.449-.808-1.95-.21-.495-.429-.505-.615-.505-.193 0-.397.005-.605.005-.199 0-.535.088-.804.374-.266.294-1.038 1.012-1.038 2.467 0 1.463 1.06 2.875 1.209 3.074.149.198 2.096 3.201 5.077 4.518.717.306 1.277.485 1.711.622.718.227 1.372.195 1.888.118.577-.086 1.774-.722 2.025-1.422.25-.7.25-1.301.175-1.422-.073-.119-.267-.192-.567-.346M15.97 27.26h-.012c-1.99 0-3.94-.535-5.645-1.55l-.406-.242-4.196 1.1 1.119-4.095-.263-.419c-1.114-1.77-1.703-3.814-1.704-5.928 0-6.125 4.989-11.112 11.118-11.112 2.971 0 5.764 1.159 7.864 3.26 2.1 2.102 3.256 4.897 3.254 7.869-.003 6.124-4.986 11.116-11.13 11.116ZM27.272 4.722C24.252 1.699 20.222.016 15.95 0 7.144 0 .007 7.134.006 15.934c0 2.81.74 5.56 2.146 7.98L.08 32l8.252-2.163a15.893 15.893 0 0 0 7.612 1.94h.007c8.805 0 15.938-7.138 15.944-15.943 0-4.26-1.653-8.292-4.672-11.31" />
        </svg>
      </span>
    </motion.a>
  );
};

// Main App Component
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState('es');
  
  const t = translations[lang];

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 50);

      // Hide on scroll down past threshold, show on scroll up
      if (currentScrollY > 120 && currentScrollY > lastScrollY) {
        setHidden(true);
      } else if (currentScrollY < lastScrollY) {
        setHidden(false);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <div className="App" data-testid="app">
        <Header 
          scrolled={scrolled} 
          hidden={hidden && !mobileMenuOpen}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
        <main>
          <Hero />
          <WhyUs />
          <About />
          <Schedule />
          <Memberships />
          <CoachesSection />
          <TestimonialsSection />
          <FAQSection />
          <Contact />
          <CTASection />
        </main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </LanguageContext.Provider>
  );
}

export default App;
